import { useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { jobRemaining } from '@/lib/finance';
import Badge from '@/components/base/Badge';
import PaymentForm from '@/pages/payments/components/PaymentForm';

interface FollowItem {
  key: string;
  customerName: string;
  reason: string;
  waiting: string;
  lastContact: string;
  jobValue?: number;
  collected?: number;
}

interface Category {
  id: string;
  title: string;
  icon: string;
  tone: string;
  items: FollowItem[];
}

export default function Takip() {
  const { requests, quotes, jobs, payments, customers } = useAppStore();
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentCustomer, setPaymentCustomer] = useState('');
  const [paymentJobId, setPaymentJobId] = useState('');

  const videoWaiting = requests
    .filter((r) => r.videoStatus === 'Video Bekleniyor' || r.videoStatus === 'Video Yok')
    .map((r) => ({ key: r.id, customerName: r.customerName, reason: r.service, waiting: 'Video bekleniyor', lastContact: r.lastContact }));

  const videoNoQuote = requests
    .filter((r) => r.videoStatus === 'Video Geldi' && ['Video Geldi', 'Değerlendiriliyor', 'Teklif Hazırlanacak'].includes(r.status))
    .map((r) => ({ key: r.id, customerName: r.customerName, reason: r.service, waiting: 'Teklif hazırlanmadı', lastContact: r.lastContact }));

  const quoteNoReply = quotes
    .filter((q) => ['Cevap Bekleniyor', 'Müşteriye Gönderildi'].includes(q.status))
    .map((q) => ({ key: q.id, customerName: q.customerName, reason: q.service, waiting: 'Cevap yok', lastContact: '2 gün önce' }));

  const oldCustomers = customers
    .filter((c) => c.status === 'Pasif')
    .map((c) => ({ key: c.id, customerName: c.name, reason: c.lastService, waiting: 'Uzun süredir pasif', lastContact: c.lastContact }));

  const pendingCollection = jobs
    .filter((j) => j.status === 'Kapandı' && jobRemaining(j, payments) > 0)
    .map((j) => ({
      key: j.id,
      customerName: j.customerName,
      reason: j.service,
      waiting: 'Tahsilat bekleniyor',
      lastContact: j.date,
      jobValue: j.jobValue,
      collected: j.jobValue - jobRemaining(j, payments),
    }));

  const categories: Category[] = [
    { id: 'video', title: 'Video Göndermeyenler', icon: 'ri-video-line', tone: 'bg-amber-50 text-amber-600', items: videoWaiting },
    { id: 'quote', title: 'Video Geldi — Teklif Hazırlanmadı', icon: 'ri-file-add-line', tone: 'bg-red-50 text-red-600', items: videoNoQuote },
    { id: 'reply', title: 'Teklif Gönderildi — Cevap Yok', icon: 'ri-chat-3-line', tone: 'bg-amber-50 text-amber-600', items: quoteNoReply },
    { id: 'old', title: 'Eski Müşteri Takibi', icon: 'ri-user-heart-line', tone: 'bg-primary-50 text-primary-600', items: oldCustomers },
    { id: 'collect', title: 'Tahsilat Bekleyenler', icon: 'ri-wallet-3-line', tone: 'bg-red-50 text-red-600', items: pendingCollection },
  ];

  function markComplete(key: string) {
    setCompleted((prev) => new Set(prev).add(key));
  }

  function openPayment(item: FollowItem) {
    setPaymentCustomer(item.customerName);
    setPaymentJobId(item.key);
    setPaymentOpen(true);
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
          <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
          Müşteri
        </div>
        <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Takip Gerekenler</h1>
        <p className="mt-1 text-sm text-foreground-500">Aksiyon bekleyen müşterileri ve tahsilatları takip edin.</p>
      </div>

      {categories.map((cat) => {
        const visible = cat.items.filter((i) => !completed.has(i.key));
        if (visible.length === 0) return null;
        return (
          <div key={cat.id} className="overflow-hidden rounded-lg border border-background-200 bg-white">
            <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${cat.tone}`}>
                  <i className={`${cat.icon} text-base`} />
                </span>
                <h2 className="font-heading text-[15px] font-bold text-foreground-950">{cat.title}</h2>
              </div>
              <Badge tone="neutral">{visible.length}</Badge>
            </div>
            <ul className="divide-y divide-background-100">
              {visible.map((item) => (
                <li key={item.key} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground-900">{item.customerName}</div>
                    <div className="text-[13px] text-foreground-500">{item.reason}</div>
                    <div className="mt-0.5 text-xs text-foreground-400">
                      {item.waiting} · Son iletişim: {item.lastContact}
                    </div>
                    {item.jobValue !== undefined && (
                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
                        <span className="text-foreground-500">
                          İş Bedeli: <span className="font-semibold text-foreground-800">{formatTL(item.jobValue)}</span>
                        </span>
                        <span className="text-foreground-500">
                          Tahsil: <span className="font-semibold text-emerald-600">{formatTL(item.collected ?? 0)}</span>
                        </span>
                        <span className="text-foreground-500">
                          Kalan:{' '}
                          <span className="font-semibold text-amber-600">{formatTL((item.jobValue ?? 0) - (item.collected ?? 0))}</span>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <a
                      href={`https://wa.me/90${(customers.find((c) => c.name === item.customerName)?.phone ?? '').replace(/\s/g, '').slice(1)}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:bg-background-100"
                    >
                      <i className="ri-whatsapp-line" /> WhatsApp
                    </a>
                    {item.jobValue !== undefined ? (
                      <button
                        type="button"
                        onClick={() => openPayment(item)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600"
                      >
                        <i className="ri-add-line" /> Ödeme Kaydet
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => markComplete(item.key)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:bg-background-100"
                      >
                        <i className="ri-phone-line" /> Arama Notu
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => markComplete(item.key)}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                    >
                      <i className="ri-check-line" /> Tamamlandı
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {categories.every((c) => c.items.every((i) => completed.has(i.key))) && (
        <div className="rounded-lg border border-background-200 bg-white px-6 py-12 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
            <i className="ri-check-double-line text-2xl" />
          </div>
          <h3 className="mt-4 font-heading text-[15px] font-bold text-foreground-900">Her şey takip edildi</h3>
          <p className="mt-1 text-sm text-foreground-500">Şu an bekleyen takip konusu bulunmuyor.</p>
        </div>
      )}

      {paymentOpen && (
        <PaymentForm
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          prefillCustomer={paymentCustomer}
          prefillJobId={paymentJobId}
        />
      )}
    </div>
  );
}